import { Box3, Matrix4, Ray, Vector3, Box2, Vector2, Color } from "three";
import { FrustumUtil, PolygonUtils } from "../utils/math-utils";
import { IPointcloudSectionSelection, PointCloud } from "./PointCloud";

export interface IPointCloudTile {
  id: number;
  url: string;
  size: number;
  isLoaded: boolean;
  frustum: FrustumUtil.TileIntersectsFrustum;
  pointcloud?: PointCloud;
  children: IPointCloudTile[];
  parent?: IPointCloudTile;
  tileBBox: Box3;
  boundingBox: Box3;
  isVisible: boolean;
  setVisibility(visibility: boolean): void;
  loadingStatus:
    | "loaded"
    | "loading"
    | "removed"
  applyTransform(matrix: Matrix4, includeChildren?: boolean): void;
  intersects(ray: Ray): boolean;
  isParent(tile: IPointCloudTile): boolean;
  isChild(tile: IPointCloudTile): boolean;
  isSibling(tile: IPointCloudTile): boolean;
  applyHeightBasedColor(
    colorData: IPointColorRangeInfo,
    polygon: { vertices: Vector3[]; indices: number[] },
    polygonbbox: Box2,
    includeChildren?: boolean
  ): void;
  setHeatMapVisibility(visibility: boolean): void;
  setPointSize(size: number): void;
  // highlightSelectedPointCloudSection(selectionPlanes:IPointcloudSectionSelection, color:Color):void;
  // resetHighlightSelectedPointCloudSection():void;
  removeSelectedPoints(selectionColor: Color): void;
  resetColor(): void;
}

export interface IPointColorRangeInfo {
  upper: {
    min: { val: number; color: Color }; //yellow
    max: { val: number; color: Color }; //red
  };
  lower: {
    min: { val: number; color: Color }; //blue
    max: { val: number; color: Color }; //skyblue
  };
}

export class PointCloudTile implements IPointCloudTile {
  id: number;
  url: string;
  size: number;
  children: IPointCloudTile[];
  parent?: IPointCloudTile;
  // tileBBox: Box3;
  boundingBox: Box3;
  isLoaded: boolean;
  frustum: FrustumUtil.TileIntersectsFrustum;
  pointcloud?: PointCloud;
  loadingStatus:
    | "loaded"
    | "loading"
    | "removed";

  private _tileBBox: Box3;
  private _tileBBox2D: Box2;
  // private _originalColors: Float32Array;
  public isVisible: boolean;

  set tileBBox(bbox: Box3) {
    this._tileBBox.copy(bbox);
    this._tileBBox2D.set(
      new Vector2(bbox.min.x, bbox.min.y),
      new Vector2(bbox.max.x, bbox.max.y)
    );
  }

  get tileBBox(): Box3 {
    return this._tileBBox;
  }

  constructor(parent?: IPointCloudTile) {
    this.children = [];
    this.parent = parent;
    this._tileBBox = new Box3();
    this._tileBBox2D = new Box2();
    this.boundingBox = new Box3();
    this.isLoaded = false;
    this.isVisible = true;
    this.loadingStatus = "removed";
  }

  intersects(ray: Ray): boolean {
    return ray.intersectsBox(this.boundingBox);
  }

  applyTransform(matrix: Matrix4, includeChildren: boolean = true): void {
    this._tileBBox.applyMatrix4(matrix);
    this._tileBBox2D.set(
      new Vector2(this._tileBBox.min.x, this._tileBBox.min.y),
      new Vector2(this._tileBBox.max.x, this._tileBBox.max.y)
    );
    this.boundingBox.applyMatrix4(matrix);
    if (includeChildren) {
      for (const child of this.children) {
        child.applyTransform(matrix, includeChildren);
      }
    }
  }

  public isParent(tile: IPointCloudTile): boolean {
    if (this.parent) {
      if (this.parent === tile) {
        return true;
      }
      return this.parent.isParent(tile);
    }
    return false;
  }

  public isChild(tile: IPointCloudTile): boolean {
    for (const child of this.children) {
      if (child === tile) {
        return true;
      }
      if (child.isChild(tile)) {
        return true;
      }
    }
    return false;
  }

  public isSibling(tile: IPointCloudTile): boolean {
    if (this === tile) {
      return false;
    }
    if (this.parent) {
      for (const child of this.parent.children) {
        if (child === tile) {
          return true;
        }
      }
    }
    return false;
  }

  //interpolated color should be applied to points based on height inside the polygon
  public applyHeightBasedColor(
    colorData: IPointColorRangeInfo,
    polygon: { vertices: Vector3[]; indices: number[] },
    polygonbbox: Box2,
    includeChildren: boolean = true
  ) {
    if (
      PolygonUtils.isBoxInsidePolygon2D(
        this._tileBBox2D,
        polygon.vertices,
        polygonbbox
      )
    ) {
      this.pointcloud?.applyHeightBasedColor(colorData, polygon, polygonbbox);
      if (includeChildren) {
        for (const child of this.children) {
          child.applyHeightBasedColor(
            colorData,
            polygon,
            polygonbbox,
            includeChildren
          );
        }
      }
    } else {
      // if (this.pointcloud) {
      //   this.pointcloud.resetColor();
      // }
    }
  }

  public setHeatMapVisibility(visibility: boolean): void {
    this.pointcloud?.setHeatMapVisibility(visibility);
    for (const child of this.children) {
      child.setHeatMapVisibility(visibility);
    }
  }

  public setVisibility(visibility: boolean): void {
    this.pointcloud?.setVisibility(visibility);
    for (const child of this.children) {
      child.setVisibility(visibility);
    }
    this.isVisible = visibility;
  }

  public setPointSize(size: number) {
    this.pointcloud?.setPointSize(size);
    for (const child of this.children) {
      child.setPointSize(size);
    }
  }

  // public highlightSelectedPointCloudSection(selectionPlanes:IPointcloudSectionSelection, color: Color){
  //   // this.pointcloud?.highlightSelectedPointCloudSection(selectionPlanes, color);
  //   for (const child of this.children) {
  //     child.highlightSelectedPointCloudSection(selectionPlanes, color);
  //   }
  // }

  // public resetHighlightSelectedPointCloudSection(){
  //   this.pointcloud?.resetHighlightSelectedPointCloudSection();
  // }

  public removeSelectedPoints(selectionColor: Color) {
    this.pointcloud?.removeSelectedPoints(selectionColor);
  }

  public resetColor(): void {
    this.pointcloud?.resetColor();
  }
}
